import { memo, useCallback } from 'react';
import isEqual from 'react-fast-compare';

import { type Clip } from '@air/api/types';

import { KanbanAssetComponent } from '~/components/KanbanView/KanbanColumnsManagers/shared/KanbanColumnItems/KanbanItem/shared/KanbanAssetComponent';
import { type KanbanAssetProps } from '~/components/KanbanView/KanbanColumnsManagers/shared/KanbanColumnItems/KanbanItem/shared/kanbanItemTypes';
import { useGetPrivateAssetMenuOptionsCallback } from '~/components/PrivateGallery/useGetPrivateAssetMenuOptionsCallback';
import { QueryParamNames } from '~/constants/search';
import { useClipPermissions } from '~/hooks/useClipPermissions';
import { useGoToPrivateAssetPage } from '~/hooks/useGoToPrivateAssetPage';
import { useOpenAssetInNewTab } from '~/hooks/useOpenAssetInNewTab';
import { usePrivateSelectionMenuOptions } from '~/hooks/usePrivateSelectionMenuOptions';
import { AssetModalPanel } from '~/providers/AssetModalPanelContextProvider';
import { usePrivateCustomFieldsContext } from '~/providers/PrivateCustomFieldsProvider';
import { isMobileAgent } from '~/utils/PlatformHelpers';
import {
  canChangeAssetCustomFields,
  canSeeAssetCustomFields,
  canViewAssetDiscussions,
} from '~/utils/permissions/assetPermissions';

export const PrivateKanbanAsset = memo(({ cardHeight, asset, kanbanDndItemData, style }: KanbanAssetProps<Clip>) => {
  const { clipPermissions } = useClipPermissions({
    clipId: asset.id,
  });

  const { customFields } = usePrivateCustomFieldsContext();
  const { getSelectionMenuOptions } = usePrivateSelectionMenuOptions();
  const { getAssetMenuOptionsCallback } = useGetPrivateAssetMenuOptionsCallback();
  const _getItemMenuOptions = getAssetMenuOptionsCallback({ item: asset });
  const { goToAssetPage } = useGoToPrivateAssetPage();
  const { openAssetInNewTab } = useOpenAssetInNewTab();

  const onCustomFieldsClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        query: {
          [QueryParamNames.amTab]: AssetModalPanel.FIELDS,
        },
        trackLocation: 'custom-field-click',
      }),
    [asset, goToAssetPage],
  );

  const onDiscussionsClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        query: {
          [QueryParamNames.amTab]: AssetModalPanel.COMMENTS,
        },
        trackLocation: 'comments-click',
      }),
    [asset, goToAssetPage],
  );

  const onVersionsClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        query: {
          [QueryParamNames.versions]: true,
        },
        trackLocation: 'versions-click',
      }),
    [asset, goToAssetPage],
  );

  const onItemClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        trackLocation: 'card-click',
      }),
    [asset, goToAssetPage],
  );
  const onItemCmdClick = useCallback(
    () =>
      openAssetInNewTab({
        asset,
        trackLocation: 'card-click',
      }),
    [asset, openAssetInNewTab],
  );

  const canViewCustomFields = canSeeAssetCustomFields(clipPermissions);
  const canSeeDiscussions = canViewAssetDiscussions(clipPermissions);
  const canChangeCustomFields = canChangeAssetCustomFields(clipPermissions);

  return (
    <KanbanAssetComponent
      cardHeight={cardHeight}
      clip={asset}
      getItemMenuOptions={_getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      isSelectable={false}
      kanbanDndItemData={kanbanDndItemData}
      onAssetClick={onItemClick}
      onAssetTitleCmdClick={onItemCmdClick}
      onCustomFieldsClick={onCustomFieldsClick}
      onDiscussionsClick={onDiscussionsClick}
      onVersionsClick={onVersionsClick}
      style={style}
      dndDisabled={isMobileAgent || !canChangeCustomFields}
      canViewCustomFields={canViewCustomFields}
      allCustomFields={customFields}
      shouldShowDiscussions={canSeeDiscussions}
      showVersionsIndicator={!!asset.assetVersionCount && asset.assetVersionCount > 1}
    />
  );
}, isEqual);

PrivateKanbanAsset.displayName = 'PrivateKanbanAsset';
